var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __pow = Math.pow;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));

// src/components/DotGrid.tsx
import { uuid } from "@ballpark/design-creator";
import { useWeakView } from "@ballpark/design-plugin-view";

// ../../internal/theme/dist/index.js
function hexToRGB(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (alpha) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgb(${r}, ${g}, ${b})`;
}
var baseColors = {
  marvelDark: "#009EEB",
  marvel: "#1FB6FF",
  marvelLight: "#89DCFF",
  marvelExtraLight: "#B7EAFB",
  greenDark: "#44C553",
  green: "#60D956",
  greenLight: "#87DC72",
  greenExtraLight: "#ADEA9E",
  orange: "#FD6F3C",
  orangeLight: "#FF977A",
  orangeExtraLight: "#FFB6A1",
  orangeExtraExtraLight: "#FFCEBD",
  yellowDark: "#A36300",
  yellow: "#FFAE30",
  yellowLight: "#FFC772",
  yellowExtraLight: "#FFD79B",
  yellowExtraExtraLight: "#FFE3B7",
  purple: "#AD6EDD",
  purpleLight: "#C896EA",
  purpleExtraLight: "#D6AFF1",
  purpleExtraExtraLight: "#DFC7F0",
  teal: "#60D2C9",
  tealLight: "#8FDFDA",
  tealExtraLight: "#BBEDED",
  tealExtraExtraLight: "#DDF6F6",
  licorice: "#0C1014",
  black: "#222D39",
  steel: "#2A3440",
  slate: "#3E4854",
  silver: "#8792A1",
  smokeExtraDark: "#AAB5C5",
  smokeDark: "#C3CCD7",
  smoke: "#D5DCE3",
  snowExtraDark: "#E6E9EF",
  snowDark: "#F0F2F6",
  snow: "#F9FAFB",
  white: "#FFFFFF",
  red: "#F95849",
  redDark: "#E65143",
  twitter: "#55acee",
  facebook: "#3b5999",
  dribbble: "#ea4c89",
  dropbox: "#007ee5",
  google: "#dc4e41",
  instagram: "#3f729b",
  linkedin: "#0077b5",
  pocket: "#ef4056",
  github: "#333",
  youtube: "#FF0000"
};
var green005 = hexToRGB(baseColors.green, 0.05);
var green008 = hexToRGB(baseColors.green, 0.08);
var colors = __spreadProps(__spreadValues({}, baseColors), {
  // We now use Radix colors, which are in the HSL format, so using the hexToRGB
  // util outside of the theme to generate opaque colors is no longer an option.
  // Moving these as real colors in the theme meanwhile to make old components work
  green005,
  green008
});
var black0006 = hexToRGB(colors.black, 6e-3);
var black002 = hexToRGB(colors.black, 0.02);
var black003 = hexToRGB(colors.black, 0.03);
var black005 = hexToRGB(colors.black, 0.05);
var black008 = hexToRGB(colors.black, 0.08);
var black01 = hexToRGB(colors.black, 0.1);
var black012 = hexToRGB(colors.black, 0.12);
var black018 = hexToRGB(colors.black, 0.18);
var black03 = hexToRGB(colors.black, 0.3);
var licorice008 = hexToRGB(colors.licorice, 0.08);
var licorice01 = hexToRGB(colors.licorice, 0.1);
var white04 = hexToRGB(colors.white, 0.4);
var white05 = hexToRGB(colors.white, 0.5);
var shadows = {
  borders: {
    top: `0 -1px 0 0 ${black018}`,
    right: `1px 0 0 0 ${black018}`,
    bottom: `0 1px 0 0 ${black018}`,
    left: `-1px 0 0 0 ${black018}`,
    inset: `inset 0 0 0 1px ${colors.smoke}`,
    insetLight: `inset 0 0 0 1px ${colors.snowExtraDark}`
  },
  button: {
    disabled: `inset 1px 0 0 ${colors.smoke}, inset -1px 0 0 ${colors.smoke}, inset 0 1px 0 ${colors.smoke}, inset 0 -1px 0 ${colors.smokeDark}, 0 1px 2px 0 rgba(12,16,20,0.10)`,
    ghost: `inset 1px 0 0 ${colors.smoke}, inset -1px 0 0 ${colors.smoke}, inset 0 1px 0 ${colors.smoke}, inset 0 -1px 0 ${colors.smokeDark}, 0 1px 2px 0 rgba(12,16,20,0.10)`,
    ghostHover: `inset 1px 0 0 ${colors.smoke}, inset -1px 0 0 ${colors.smoke}, inset 0 1px 0 ${colors.smoke}, inset 0 -1px 0 ${colors.smokeDark}, inset 0 0 0 1px ${colors.smoke}, 0 2px 4px 0 rgba(12,16,20,0.10)`,
    ghostReversed: `inset 0 0 0 1px ${white04}`,
    ghostReversedHover: `inset 0 0 0 1px rgba(255,255,255,0)`,
    withShadow: `0 0 0 1px ${licorice008}, 0 1px 3px 0 ${licorice008}`,
    toolbar: `0 0 0 1px ${black008}`,
    green: `inset 1px 0 0 ${black002}, inset -1px 0 0 ${black002}, inset 0 1px 0 ${black002}, inset 0 -1px 0 ${black003}, inset 0 0 0 1px ${black002}, 0 1px 2px 0 rgba(25,64,22,0.10)`,
    greenHover: `inset 1px 0 0 ${black002}, inset -1px 0 0 ${black002}, inset 0 1px 0 ${black002}, inset 0 -1px 0 ${black003}, inset 0 0 0 1px ${black002}, 0 2px 4px 0 rgba(25,64,22,0.10)`,
    marvel: `inset 1px 0 0 ${black002}, inset -1px 0 0 ${black002}, inset 0 1px 0 ${black002}, inset 0 -1px 0 ${black003}, inset 0 0 0 1px ${black002}, 0 1px 2px 0 rgba(2,47,69,0.10)`,
    marvelHover: `inset 1px 0 0 ${black002}, inset -1px 0 0 ${black002}, inset 0 1px 0 ${black002}, inset 0 -1px 0 ${black003}, inset 0 0 0 1px ${black002}, 0 2px 4px 0 rgba(2,47,69,0.10)`,
    danger: `inset 1px 0 0 ${black002}, inset -1px 0 0 ${black002}, inset 0 1px 0 ${black002}, inset 0 -1px 0 ${black003}, inset 0 0 0 1px ${black002}, 0 1px 2px 0 rgba(53,17,13,0.10)`,
    dangerHover: `inset 1px 0 0 ${black002}, inset -1px 0 0 ${black002}, inset 0 1px 0 ${black002}, inset 0 -1px 0 ${black003}, inset 0 0 0 1px ${black002}, 0 2px 4px 0 rgba(81,25,19,0.10)`,
    text: "0 1px 0 rgba(12,16,20,0.05)",
    // A separate loading state shadow is needed for askhub
    loading: `inset 1px 0 0 ${colors.smoke}, inset -1px 0 0 ${colors.smoke}, inset 0 1px 0 ${colors.smoke}, inset 0 -1px 0 ${colors.smokeDark}, 0 1px 2px 0 rgba(12,16,20,0.10)`
  },
  select: {
    disabled: `inset 0 0 0 1px ${colors.smoke}`,
    ghost: `inset 0 0 0 1px ${colors.smoke}`,
    ghostFocus: `inset 0 0 0 1px ${colors.marvel}`,
    ghostHover: `inset 0 0 0 1px ${colors.smokeExtraDark}`,
    error: `inset 0 0 0 1px ${colors.red}`
  },
  input: {
    default: `inset 0 0 0 1px ${colors.smoke}`,
    defaultTransparent: `0 0 0 1px ${black018}`,
    defaultHover: `inset 0 0 0 1px ${colors.smokeExtraDark}`,
    defaultFocus: `inset 0 0 0 1px ${colors.marvel}`,
    whiteTransparentHover: `inset 0 0 0 1px ${white05}`,
    whiteTransparentFocus: `inset 0 0 0 1px ${colors.white}`,
    error: `inset 0 0 0 1px ${colors.red}`,
    disabled: `inset 0 0 0 1px ${colors.smoke}`,
    underline: `inset 0 -1px 0 ${colors.smoke}`,
    underlineHover: `inset 0 -1px 0 ${colors.smokeExtraDark}`,
    underlineFocus: `inset 0 -1px 0 ${colors.marvel}`
  },
  popover: {
    default: `0 0 1px 0 ${black03}, 0 2px 6px 0 ${black01}`,
    raised: `0 0 0 1px ${black008}, 0 8px 10px 0 ${black008}, 0 8px 24px 0 ${black005}`
  },
  checkbox: {
    default: `inset 0 0 0 1px ${colors.smoke}`,
    defaultChecked: `inset 0 0 0 50% ${colors.green}`
  },
  image: {
    default: `0 0 0 1px ${black012}`,
    selected: `0 0 0 2px ${colors.white}, 0 0 0 4px ${colors.green}`
  },
  modal: `0 6px 12px 0 ${licorice008}`,
  raiseOnHover: `0 0 0 1px ${black0006}, 0 2px 4px 0 ${black003}, 0 8px 16px 0 ${black01}`,
  toast: `0 2px 6px 0 ${licorice01}, 0 3px 15px 0 ${licorice008}`,
  toggleButton: {
    default: `inset 0 0 0 1px ${colors.smoke}`,
    selected: `inset 0 0 0 2px ${colors.marvel}`
  }
};
var easings = {
  bouncy: {
    string: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
    array: [0.68, -0.55, 0.265, 1.55]
  },
  smooth: {
    string: "cubic-bezier(0.23, 1, 0.32, 1)",
    array: [0.23, 1, 0.32, 1]
  }
};
var transitions = {
  smooth: {
    fast: `all 300ms ${easings.smooth.string}`,
    medium: `all 600ms ${easings.smooth.string}`,
    slow: `all 900ms ${easings.smooth.string}`
  }
};

// src/components/DotGrid.tsx
import { observer } from "mobx-react";
import { useState as useState2 } from "react";

// src/react.tsx
import { usePropReaction } from "@ballpark/core";
import { createContext, useContext, useState } from "react";
import { useMemoOne } from "use-memo-one";

// src/Grid.ts
import { makeAutoObservable } from "mobx";
var Grid = class {
  constructor({ size = 16, enabled = true }) {
    this.size = size;
    this.enabled = enabled;
    makeAutoObservable(this);
  }
};

// src/react.tsx
import { Fragment, jsx } from "react/jsx-runtime";
var GridContext = createContext(null);
function useGrid() {
  const state = useContext(GridContext);
  if (state === null)
    throw new Error("useGrid() called outside of GridProvider");
  return state;
}
function useWeakGrid(options) {
  const maybeGrid = useContext(GridContext);
  const grid = useMemoOne(
    () => maybeGrid != null ? maybeGrid : new Grid(__spreadValues({ enabled: false }, options)),
    [maybeGrid]
  );
  return grid;
}
function GridProvider({
  children,
  options = {}
}) {
  const [grid] = useState(() => new Grid(options));
  usePropReaction((newOptions) => {
    Object.assign(grid, newOptions);
  }, options);
  return /* @__PURE__ */ jsx(GridContext.Provider, { value: grid, children });
}
function WeakGridProvider({
  children,
  options
}) {
  const state = useContext(GridContext);
  if (state === null) {
    return /* @__PURE__ */ jsx(GridProvider, { options, children });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}

// src/components/DotGrid.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var DotGrid = observer(function DotGrid2() {
  const { size } = useWeakGrid({ enabled: true });
  const { zoomFactor, matrix } = useWeakView();
  const [id] = useState2(() => `gridDot-${uuid()}`);
  const gridScaleFactor = getGridScaleFactorAtZoom(zoomFactor);
  const scaledGridSize = getScaledGridSize(size, gridScaleFactor);
  const fill = colors.smokeDark;
  const r = Math.max(1, 0.5 / zoomFactor);
  const circleProps = { r, fill };
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      height: "100%",
      width: "100%",
      style: { position: "absolute" },
      children: [
        /* @__PURE__ */ jsx2("defs", { children: /* @__PURE__ */ jsxs(
          "pattern",
          {
            height: scaledGridSize,
            id,
            patternTransform: `matrix(${matrix.join(",")})`,
            patternUnits: "userSpaceOnUse",
            width: scaledGridSize,
            children: [
              /* @__PURE__ */ jsx2("circle", __spreadValues({ cx: 0, cy: 0 }, circleProps)),
              /* @__PURE__ */ jsx2("circle", __spreadValues({ cx: scaledGridSize, cy: 0 }, circleProps)),
              /* @__PURE__ */ jsx2("circle", __spreadValues({ cx: scaledGridSize, cy: scaledGridSize }, circleProps)),
              /* @__PURE__ */ jsx2("circle", __spreadValues({ cx: 0, cy: scaledGridSize }, circleProps))
            ]
          }
        ) }),
        /* @__PURE__ */ jsx2("rect", { fill: `url(#${id})`, height: "100%", width: "100%" })
      ]
    }
  );
});
function getScaledGridSize(gridSize, gridScaleFactor) {
  return Math.floor(gridSize * gridScaleFactor);
}
function getGridScaleFactorAtZoom(zoom) {
  if (zoom >= 1) {
    return 1;
  }
  const exponent = Math.log2(1 / zoom);
  return __pow(2, Math.floor(exponent));
}
export {
  DotGrid,
  Grid,
  GridProvider,
  WeakGridProvider,
  useGrid,
  useWeakGrid
};
